/**
 * Onboarding tour — first-time spotlight.
 *
 * Shows a centered card walking through the four primary surfaces:
 *   Dashboard → Customers → Platform bugs → Tickets
 *
 * Persists completion in localStorage so it doesn't reappear. Includes
 * a "Skip" button and stores the same flag on skip so the tour stays
 * out of the way for returning operators. The "Take me there" button
 * closes the tour and routes to the highlighted page.
 */
import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, X, ArrowRight, Activity, Users, Bug, Ticket, Check,
} from 'lucide-react';
import { Button } from '../ui/Button';

const TOUR_KEY = 'vloud:tour_complete';

interface Stop {
  title: string;
  body: string;
  icon: typeof Activity;
  tone: string;
  href: string;
}

const STOPS: Stop[] = [
  {
    title: 'Dashboard',
    body: 'Operations health: customer counts, active licenses, heartbeats, anomalies, plus recent activity and quick actions.',
    icon: Activity,
    tone: '#818cf8',
    href: '/',
  },
  {
    title: 'Customers',
    body: 'Browse the entire account roster. Click any row to open the drawer with notes, license history, and server attribution.',
    icon: Users,
    tone: '#c084fc',
    href: '/customers',
  },
  {
    title: 'Platform bugs',
    body: 'Engine crash triage. Switch between list and kanban, drag cards across columns, set the fixed-in-version to surface the fix in customer update-checks.',
    icon: Bug,
    tone: '#f87171',
    href: '/platform-bugs',
  },
  {
    title: 'Tickets',
    body: 'Customer support inbox. Filter by priority + category, watch the per-row SLA chip, and use saved replies for fast responses.',
    icon: Ticket,
    tone: '#22d3ee',
    href: '/tickets',
  },
];

export function OnboardingTour(): JSX.Element | null {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      if (localStorage.getItem(TOUR_KEY) === '1') return undefined;
      // First load — defer slightly so the dashboard paints before the
      // tour overlays it.
      const id = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(id);
    } catch {
      // localStorage may be disabled — surface the tour rather than block.
      setOpen(true);
      return undefined;
    }
  }, []);

  const finish = (): void => {
    setOpen(false);
    try { localStorage.setItem(TOUR_KEY, '1'); }
    catch { /* ignore */ }
  };

  if (!open) return null;
  const stop = STOPS[step]!;
  const last = step === STOPS.length - 1;
  const Icon = stop.icon;

  const goTo = (): void => {
    finish();
    nav(stop.href);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome tour"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={finish}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(11, 15, 25, 0.7)',
          backdropFilter: 'blur(6px)',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: 'min(480px, 100%)',
          padding: 24,
          borderRadius: 18,
          background: 'var(--bg-raised)',
          border: '1px solid var(--border)',
          boxShadow: '0 32px 80px rgba(0, 0, 0, 0.55)',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              fontSize: '0.6875rem',
              padding: '2px 8px',
              borderRadius: 999,
              background: 'rgba(99, 102, 241, 0.15)',
              border: '1px solid rgba(99, 102, 241, 0.35)',
              color: 'var(--accent-text)',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            <Sparkles width={11} height={11} style={{ display: 'inline', marginRight: 4 }} />
            Welcome tour
          </span>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: '0.6875rem', color: 'var(--text-muted)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {step + 1} of {STOPS.length}
          </span>
          <button
            type="button"
            onClick={finish}
            aria-label="Skip tour"
            style={{
              background: 'transparent', border: 'none',
              padding: 4, borderRadius: 6,
              color: 'var(--text-muted)', cursor: 'pointer',
            }}
          >
            <X width={16} height={16} />
          </button>
        </header>

        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 14,
          }}
        >
          <div
            aria-hidden
            style={{
              flex: 'none',
              width: 56, height: 56, borderRadius: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `${stop.tone}1f`,
              border: `1px solid ${stop.tone}55`,
              color: stop.tone,
            }}
          >
            <Icon width={26} height={26} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                margin: 0, marginBottom: 4,
                fontSize: '1.125rem', fontWeight: 600,
              }}
            >
              {stop.title}
            </h2>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {stop.body}
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'flex', gap: 6, alignItems: 'center',
            paddingTop: 4,
          }}
        >
          {STOPS.map((_, i) => (
            <span
              key={i}
              aria-hidden
              style={{
                width: i === step ? 18 : 6,
                height: 6,
                borderRadius: 999,
                background: i === step ? stop.tone : 'var(--bg-overlay)',
                border: i < step ? '1px solid rgba(52, 211, 153, 0.35)' : '1px solid var(--border)',
                transition: 'width 200ms ease, background 160ms ease',
              }}
            />
          ))}
        </div>

        <footer
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            paddingTop: 8,
            borderTop: '1px solid var(--border)',
          }}
        >
          <Button variant="ghost" size="sm" onClick={finish}>
            Skip
          </Button>
          <span style={{ flex: 1 }} />
          {step > 0 && (
            <Button variant="outline" size="sm" onClick={() => setStep((s) => s - 1)}>
              Back
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={goTo}>
            <ArrowRight width={14} height={14} />
            Take me there
          </Button>
          {last ? (
            <Button variant="default" size="sm" onClick={finish}>
              <Check width={14} height={14} />
              Done
            </Button>
          ) : (
            <Button variant="default" size="sm" onClick={() => setStep((s) => s + 1)}>
              Next
              <ArrowRight width={14} height={14} />
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}
